import { mapGetters, mapState } from 'vuex'
import { eventBus } from '@/helpers/eventBus'
import formatPrice from '@/helpers/filters/formatPrice'
import weightString from '@/helpers/filters/weightString'
import { getTranslatedProperty } from '@/helpers/getTranslatedProperty'
import { getProductLocation } from '@/helpers/getProductLocation'
import { isExpiredProduct } from '@/helpers/getters/isExpiredProduct'
import { ECOMMERCE_ACTION_TYPE, Metrics, pushEcommerce } from '@/utils/metrics'
import { MARKET_RELATED_ROUTE_NAMES } from '@/constants'
import { getAltTextAndTitleForProduct } from '@/helpers/sellerProduct/getAltTextAndTitleForProduct'
import { SELLER_PRODUCT_STATUS } from '@/constants'

export default {
  props: {
    product: {
      type: Object,
      required: true,
    },
    showParams: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    ...mapGetters('user', ['isBuyer', 'isSystemSeller']),
    ...mapState('user', ['currentUser']),
    ...mapGetters('cart', ['cartItemsProducts']),
    ...mapState('misc', ['countries']),
    ...mapGetters('auth', ['loggedIn']),
    ...mapGetters('misc', ['unitPcs']),
    ...mapGetters('localCart', ['localCartItemsProducts']),
    ...mapState('profileProduct', ['sellerProducts']),
    additionalProps() {
      const additional = [
        {
          title: this.$t(`Dimension`),
          value: this.product.caliber,
        },
        {
          title: this.$t(`Packing`),
          value: this.product.packing.name,
        },
        {
          title: this.$t(`Quantum`),
          value: this.product.quantum.toString(),
        },
      ]

      if (this.product.wt_unit.id === this.unitPcs.id && this.product.one_item_weight) {
        additional.push({
          title: this.$t('Weight of one piece'),
          value: this.product.one_item_weight + ` ${this.$t('units.weight.kg')}`,
        })
      }

      return additional
    },
    isProductInCart() {
      if (!this.loggedIn) {
        return this.localCartItemsProducts.some(p => p.id === this.product.id)
      }

      return this.cartItemsProducts.some(item => item && item.seller_product.id === this.product.id)
    },
    addToCartAttrs() {
      return {
        to: this.isProductInCart ? 'cart' : undefined,
        title: this.isProductInCart ? this.$t(`Go to cart`) : undefined,
      }
    },
    preparedImages() {
      if (this.product.images.length) {
        return this.product.images.map(img => ({
          ...img,
          ...getAltTextAndTitleForProduct(this.product),
          uri: img.image_medium || img.image || img.image_small,
        }))
      }

      return [
        {
          id: 1,
          uri: require('@/assets/images/icons/nophoto-medium.jpg'),
        },
      ]
    },
    productLocation() {
      return getProductLocation(this.product, true)
    },
    productName() {
      return this.$t(
        'marketProductName',
        [this.product.product, this.product.sort].map(param => getTranslatedProperty(param)),
      )
    },
    productSellerInfo() {
      const sellerInfo = {
        id: null,
        name: '',
        avatar: '',
        organization: {
          name: '',
          verified: false,
        },
      }

      if (this.hasSystemSeller) {
        sellerInfo.name = this.product.supplier.user.name
        ;(sellerInfo.organization.name = this.product.supplier.organization.short_title),
          (sellerInfo.organization.verified = this.product.supplier.organization.verified)
        sellerInfo.avatar = this.product.supplier.user.photo_small
        sellerInfo.id = this.product.supplier.id
      } else {
        sellerInfo.name = this.product.seller.user.name
        sellerInfo.organization.name = this.product.seller.organization.short_title
        sellerInfo.organization.verified = this.product.seller.organization.verified
        sellerInfo.avatar = this.product.seller.user.photo_small
        sellerInfo.id = this.product.seller.id
      }

      return sellerInfo
    },
    hasSystemSeller() {
      return this.product.supplier !== null
    },
    isSpecialOffer() {
      return this.product.sort_weight > 0 || this.product.discount > 0
    },
    isExpired() {
      return isExpiredProduct(this.product)
    },
    canAddToCart() {
      return (this.isBuyer || !this.loggedIn) && !this.isExpired && !this.isOutOfStock
    },
    hasVAT() {
      return this.product.tax > 0
    },
    isOutOfStock() {
      return this.product.status === SELLER_PRODUCT_STATUS.not_publish
    },
  },
  methods: {
    formatPrice,
    weightString,
    addToCart() {
      if (this.loggedIn) {
        eventBus.$emit('modal', {
          component: 'AddToCart',
          data: this.product,
        })
      } else {
        this.$store.dispatch('localCart/addToLocalCart', this.product)
        eventBus.$emit('showCartSidebar', true)
      }

      pushEcommerce({
        actionType: ECOMMERCE_ACTION_TYPE.add,
        products: [this.product],
      })
    },
    showMoreInfo() {
      // Если товар просмотрели на страницах с листингами товаров, а не в "мои продукты"
      if (MARKET_RELATED_ROUTE_NAMES.includes(this.$route.name)) {
        Metrics.reportProductClick(this.product)
      }

      const slug = this.product.routeName || `${this.product.id}`

      // ссылка на товар актуальна покупателям/гостям
      if (this.isBuyer || !this.loggedIn) {
        const productRoute = this.$router.resolve({
          name: 'productDetail',
          params: { productSlug: slug },
        }).href

        const previousUrl = window.location.pathname + window.location.search
        window.history.pushState({ previousUrl }, '', productRoute)
      }

      eventBus.$emit('modal', {
        component: 'ProductMoreInfo',
        data: { productSlug: slug },
      })
    },
    goToCart() {
      if (this.loggedIn) {
        this.$router.push({ name: 'cart' })
      } else {
        eventBus.$emit('showCartSidebar', true)
      }
    },
  },
}
